import {inject, Injectable} from '@angular/core';
import {Customer, CustomerInfo, CustomerWithVehicles} from '@service-and-repairs/calm-leads-customer-lib-api';
import {Observable} from 'rxjs';
import {DmsCustomerDetermination} from '../../../components/dms-search/dms-customer-determination';
import {DmsService} from '../../../components/dms-search/dms.service';
import {ServiceCase} from '../../service-case/models/service-case';
import {CustomerService} from './customer.service';

@Injectable({
  providedIn: 'root'
})
/**
 * Coordinates loading customer-related data into a service case (language/number, customers, plates) using DMS and
 * customer services.
 */
export class CustomerLoader {
  private readonly customerService: CustomerService = inject(CustomerService);
  private readonly dmsService: DmsService = inject(DmsService);

  /**
   * Loads the customer language and DMS customer number into the service case based on the VIN.
   * Only loads if VIN is present, customer language is not already set, and DMS order requires customer language.
   * @param serviceCase The service case to update
   * @returns Promise resolving to true if customer language was updated, false otherwise
   */
  loadCustomerLanguageAndNumberInServiceCase(serviceCase: ServiceCase): Promise<boolean> {
    if (!serviceCase.getVinLong()
      || serviceCase.getCustomerLanguage()
      || !this.dmsService.dmsOrderHasToBeInCustomerLanguage()) {
      return Promise.resolve(false);
    }

    return new Promise(resolve => {
      this.dmsService.getCustomerLanguageAndNumber(serviceCase.getVinLong())
        .then(([language, dmsCustomerNumber]: string[]): void => {
          if (language && serviceCase.getCustomerLanguage() !== language) {
            serviceCase.setCustomerLanguage(language);
            resolve(true);
          }
          if (dmsCustomerNumber) {
            serviceCase.setDmsCustomerNumber(dmsCustomerNumber);
          }
          resolve(false);
        })
        .catch((): void => resolve(false));
    });
  }

  /**
   * Fetches all customers associated with the VIN and updates the service case.
   * If AWP-DMS-Interface is enabled (i.e. the customer lib is not able to load customers from DMS, because the lib
   * only has access to legacy DMS interfaces), also fetches customers from DMS and appends them.
   * Sets a loading error on the service case if the fetch fails and clears a previous one if it succeeds.
   * @param serviceCase The service case to update with customer info
   * @returns Promise resolving to true if customers were updated, false otherwise
   */
  async loadCustomersInServiceCase(serviceCase: ServiceCase): Promise<boolean> {
    const vin: string = serviceCase.getVinLong();
    if (!vin) {
      return false;
    }

    try {
      const customerInfo: CustomerInfo = await this.customerService.fetchAllCustomers(vin);

      if (this.dmsService.isAwpDmsInterfaceEnabled()) {
        const dmsCustomers: CustomerWithVehicles[] = await this.dmsService.searchCustomersByVinViaAwpDmsInterface(vin);
        const dmsCustomerObjects: Customer[] = dmsCustomers.map((c: CustomerWithVehicles): Customer => c.customer);
        customerInfo.customers = [...customerInfo.customers, ...dmsCustomerObjects];
      }

      serviceCase.setCustomerLoadingError(undefined);
      return serviceCase.updateCustomers(customerInfo);
    } catch (error) {
      serviceCase.setCustomerLoadingError((error as Error).message);
      return false;
    }
  }

  /**
   * Searches for customers by their first and last name.
   * @param firstName The customer's first name
   * @param lastName The customer's last name
   * @returns Promise resolving to an array of customers with their vehicles
   */
  getCustomersByName(firstName: string, lastName: string): Promise<CustomerWithVehicles[]> {
    // If the AWP-DMS-Interface is enabled, the customer lib will be bypassed and the search is performed directly via
    // the DMS service. That's okay, because the customer lib only calls legacy DMS APIs without adding logic or data.
    if (this.dmsService.isAwpDmsInterfaceEnabled()) {
      return this.dmsService.searchCustomersByNameViaAwpDmsInterface(firstName, lastName);
    } else {
      return this.customerService.searchByCustomerName(firstName, lastName);
    }
  }

  /**
   * Loads the license plate from DMS into the service case based on the VIN.
   * Updates both the service case and its associated vehicle if the plate differs.
   * @param serviceCase The service case to update
   * @returns Promise resolving to true if license plate was updated, false otherwise
   */
  loadLicensePlateInServiceCase(serviceCase: ServiceCase): Promise<boolean> {
    if (!serviceCase.getVinLong()) {
      return Promise.resolve(false);
    }

    return new Promise(resolve => {
      this.dmsService.getLicensePlateByVin(serviceCase.getVinLong())
        .then((licensePlate: string) => {
            if (serviceCase.getLicensePlate() !== licensePlate) {
              serviceCase.setLicensePlate(licensePlate);
              serviceCase.getVehicle().setLicensePlate(licensePlate);
              resolve(true);
            } else {
              resolve(false);
            }
          }
        )
        .catch(() => resolve(false));
    });
  }

  /**
   * Checks whether DMS orders must be created in the customer's language.
   * @returns true if customer language check is enabled
   */
  isCustomerLanguageCheckEnabled(): boolean {
    return this.dmsService.dmsOrderHasToBeInCustomerLanguage();
  }

  /**
   * Emits whenever a single DMS customer ID was determined for a VIN via the DMS lib (e.g. selected by the user in an
   * app).
   * @returns Observable of the determined VIN and DMS customer ID
   */
  getDmsCustomerDeterminations(): Observable<DmsCustomerDetermination> {
    return this.dmsService.dmsCustomerDetermined;
  }
}
